import React, { useEffect, useRef, useState } from 'react';

import PreventLostFocus from '../../../../atoms/PreventLostFocus/PreventLostFocus';
// eslint-disable-next-line max-len
import AnnouncementMultiSelectResultList from './partials/AnnouncementMultiSelectResultList/AnnouncementMultiSelectResultList';
// eslint-disable-next-line max-len
import { PersonCollectionMultiSelectResultList } from './partials/PersonCollectionMultiSelectResultList/PersonCollectionMultiSelectResultList';
import { ContentSelectComboBox, ListBoxModifier, UserSelectComboBox } from '../ComboBox';
import { sendLiveRegionMessage } from '../../../../atoms/LiveRegionGlobal/LiveRegionGlobal';
import { useSonTranslation } from '../../../../../translation/useSonTranslation';
import { FormsComboBoxMultiSelectTypeParameter } from '../../../../../_blocks/Announcements/Announcement.types';

export enum BREED {
  PersonCollection = 'PersonCollection',
  Announcements = 'Announcements',
}

type FormsComboBoxMultiSelectType = {
  idSuffix?: string;
  label: string;
  labelHelpText?: string;
  breed: BREED;
  maxSelectableItems?: number;
  // Props from ComboboxMultiSelectFormModel
  parameter: FormsComboBoxMultiSelectTypeParameter;
};
/**
 * FormsComboBoxMultiSelect
 * Select multiple items from a combobox and renders a specific result list
 *
 * TODO: refactor this Component into three exclusive components for Announcements, PersonCollection and Keywords
 *
 * The state this Form, how data is handled and how the body for the post request is shaped is
 * all done in ComboboxMultiSelectFormModel.js
 */
export default function FormsComboBoxMultiSelect({
  idSuffix,
  label,
  labelHelpText,
  breed,
  maxSelectableItems,
  parameter,
}: FormsComboBoxMultiSelectType) {
  const { t } = useSonTranslation('FormsComboBoxMultiSelect');
  const multiSelectRef = useRef<HTMLDivElement | null>(null);
  const [lastSelectedItem, setLastSelectedItem] = useState('');
  const [selectionMessage, setSelectionMessage] = useState('');
  const [deleteMessage, setDeleteMessage] = useState('');
  const [hasSelected, setHasSelected] = useState(parameter.selectedItems?.length > 0);

  /**
   * make this dynamic via selector as property or similar!
   * informing screen reader users that what and how many was selected
   */
  const setSelectionMessageWithName = (itemName: string) => setSelectionMessage(t('selected', { item: itemName }));

  const setLastSelectedItemById = (id: string) => setLastSelectedItem(`${id}-${Math.random()}`);

  const getSuffix = () => (idSuffix ? `-${idSuffix}` : '');

  const addPersonCollectionParameter = (selectedItem: any) => {
    parameter.addParameterToSelectedList(selectedItem, maxSelectableItems, 'start');
    setLastSelectedItemById(selectedItem.id);
    setSelectionMessageWithName(selectedItem.profileNameWithoutHighlighting);
  };

  const addAnnouncementParameter = (selectedItem: any) => {
    parameter.addParameterToSelectedList({
      title: selectedItem.title,
      id: selectedItem.id,
    });
    setLastSelectedItemById(selectedItem.id);
    setSelectionMessageWithName(selectedItem.title);
  };

  /**
   * takes the result received from combobox, e.g. the solr search
   * and pushed it so the selectedItems.
   *
   * Set last selected item with the id and a Math random number.
   * this is important to differentiate between unique "clicks"
   *   -> to highlight the same item twice
   *
   * Additionally, creates a message in the live region, that item was selected.
   *
   * Via maxSelectableItems you can set limit for selectable items
   * */
  const addSelectedItemToFormModel = (selectedItem: any) => {
    if (selectedItem) {
      switch (breed) {
        case BREED.PersonCollection:
          addPersonCollectionParameter(selectedItem);
          break;
        case BREED.Announcements:
        default:
          addAnnouncementParameter(selectedItem);
          break;
      }
      setHasSelected(true);
    }
  };

  /**
   * Removing the item in the ComboboxMultiSelectFormModel
   * Informing the screen reader user in the live region
   * @param item
   */
  const removeItemByTitleAndSetLiveRegionMessage = (item: any) => {
    parameter.removeParameterByTitle(item.title);
    if (parameter.selectedItems?.length === 0) {
      setHasSelected(false);
    }
    setSelectionMessage('');
    setDeleteMessage(t('deleted', { item: item.title }));
  };

  /**
   * Removing the item in the ComboboxMultiSelectFormModel
   * Informing the screen reader user in the live region
   * @param selectedItem
   */
  const removeItemByIdAndSetLiveRegionMessage = (selectedItem: any) => {
    // Person collection has profile name, but only if it is fetched from backend initial.
    // If we go through selection of the combobox, then it is without highlighting (this needs to be fixed in backend).
    const itemThatWasDeleted =
      selectedItem.profileNameWithoutHighlighting || selectedItem.profileName || selectedItem.title;

    parameter.removeParameterById(selectedItem);
    if (parameter.selectedItems?.length === 0) {
      setHasSelected(false);
    }
    setSelectionMessage('');
    setDeleteMessage(t('deleted', { item: itemThatWasDeleted }));
  };

  useEffect(() => {
    if (selectionMessage) {
      sendLiveRegionMessage(selectionMessage);
    }
  }, [selectionMessage]);

  useEffect(() => {
    if (deleteMessage) {
      sendLiveRegionMessage(deleteMessage);
    }
  }, [deleteMessage]);

  const getByBreed = () => {
    switch (breed) {
      case BREED.PersonCollection:
        return (
          <>
            <UserSelectComboBox
              id={`combobox-multiselect-${parameter.name}${getSuffix()}`}
              name={parameter.name}
              required={parameter.required}
              maxLength={parameter.constraints?.maxLength}
              validationMessage={!hasSelected && parameter.required ? t('validationMessagePersons') : ''}
              labelTitle={label}
              labelHelpText={labelHelpText}
              suggestUrl={parameter.comboBoxUrl.url}
              ariaAutoComplete="list"
              // Events
              onSelection={(item) => {
                addSelectedItemToFormModel(item);
              }}
            />
            <PersonCollectionMultiSelectResultList
              removeParameterById={removeItemByIdAndSetLiveRegionMessage}
              onListChange={(list: any[]) => {
                parameter.onSortOrderChanged(list);
              }}
              selectedResults={parameter.selectedItems}
              hasSelectedResults={hasSelected}
              lastSelectedItem={lastSelectedItem}
            />
          </>
        );
      case BREED.Announcements:
        return (
          <>
            <ContentSelectComboBox
              id={`combobox-multiselect-${parameter.name}${getSuffix()}`}
              name={parameter.name}
              required={parameter.required}
              maxLength={parameter.constraints?.maxLength}
              labelTitle={label}
              labelHelpText={labelHelpText}
              suggestUrl={parameter.comboBoxUrl.url}
              validationMessage={!hasSelected && parameter.required ? t('validationMessageAnnouncements') : ''}
              ariaAutoComplete="list"
              // Events
              onSelection={item => addSelectedItemToFormModel(item)}
              modifierListBox={ListBoxModifier.RELATIVE} // listbox expands modal dialog
            />
            <AnnouncementMultiSelectResultList
              removeParameterByTitle={removeItemByTitleAndSetLiveRegionMessage}
              selectedResults={parameter.selectedItems}
              hasSelectedResults={hasSelected}
              lastSelectedItem={lastSelectedItem}
              showReadProtectedInformation={parameter.hasReadProtectedItems}
            />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <PreventLostFocus attachTo={multiSelectRef}>
      <div ref={multiSelectRef} className="forms-combobox-multiselect">
        {getByBreed()}
      </div>
    </PreventLostFocus>
  );
}
